import { useState, useEffect, useMemo } from 'react';
import { Sparkles, Lock, AlertCircle, Check, TrendingUp, AlertTriangle, ChevronDown } from 'lucide-react';
import Layout from '@/components/Layout';
import StrataMap from '@/components/StrataMap';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stateDetails = {
  ground: { 
    label: 'Ground', 
    color: '#5BB88A',  // Brighter green for better contrast
    colorDark: '#3D8B66',
    desc: 'Identity, values, stability, core self',
    defaultHeadline: 'Your foundational sense of self and core values.',
    defaultTags: ['Identity', 'Values', 'Stability']
  },
  observe: { 
    label: 'Observe', 
    color: '#5AAEE0',  // Brighter blue for better contrast
    colorDark: '#2B6A94',
    desc: 'Self-awareness, blind spots, derailer risks',
    defaultHeadline: 'How clearly you see yourself and potential blind spots.',
    defaultTags: ['Self-awareness', 'Blind Spots', 'Reflection']
  },
  connect: { 
    label: 'Connect', 
    color: '#D88BC4',  // Brighter plum for better contrast
    colorDark: '#9B5088',
    desc: 'Relational style, influence patterns, empathy',
    defaultHeadline: 'Your relational approach and ability to build bridges.',
    defaultTags: ['Relational Depth', 'Influence', 'Empathy']
  },
  execute: { 
    label: 'Execute', 
    color: '#F08050',  // Brighter orange for better contrast
    colorDark: '#B54D24',
    desc: 'Cognitive strength, achievement drive, decision style',
    defaultHeadline: 'Your drive to achieve and make decisive moves.',
    defaultTags: ['Achievement', 'Decision-making', 'Drive']
  },
  integrate: { 
    label: 'Integrate', 
    color: '#85B86A',  // Brighter olive for better contrast
    colorDark: '#4D7636',
    desc: 'Synthesis, wisdom, career arc, leadership maturity',
    defaultHeadline: 'Your ability to synthesize experience into wisdom.',
    defaultTags: ['Synthesis', 'Wisdom', 'Maturity']
  }
};

// Helper to split narrative into paragraphs and extract headline
function parseNarrative(narrative, maxSentences = 8) {
  if (!narrative) return { headline: '', paragraphs: [], truncated: false };
  
  // Split by sentences (roughly)
  const sentences = narrative.match(/[^.!?]+[.!?]+/g) || [narrative];
  
  // First sentence or two could be the headline
  const headline = sentences[0]?.trim() || '';
  
  // Rest is body
  const bodySentences = sentences.slice(1);
  const truncated = bodySentences.length > maxSentences;
  const displaySentences = truncated ? bodySentences.slice(0, maxSentences) : bodySentences;
  
  // Split into two paragraphs
  const midPoint = Math.ceil(displaySentences.length / 2);
  const para1 = displaySentences.slice(0, midPoint).join(' ').trim();
  const para2 = displaySentences.slice(midPoint).join(' ').trim();
  
  return {
    headline,
    paragraphs: [para1, para2].filter(p => p),
    truncated,
    fullText: bodySentences.join(' ')
  };
}

// Extract signal tags from verified signals for a state
function getStateTags(synthesis, stateKey) {
  if (!synthesis?.verified_signals) return stateDetails[stateKey]?.defaultTags || [];
  
  // Try to find signals related to this state
  const relatedSignals = synthesis.verified_signals
    .filter(s => s.signal?.toLowerCase().includes(stateKey) || 
                 s.signal?.toLowerCase().includes(stateDetails[stateKey]?.label.toLowerCase()))
    .slice(0, 3);
  
  if (relatedSignals.length > 0) {
    return relatedSignals.map(s => {
      // Extract key phrase from signal
      const words = s.signal.split(' ').slice(0, 3).join(' ');
      return words.length > 25 ? words.substring(0, 22) + '...' : words;
    });
  }
  
  return stateDetails[stateKey]?.defaultTags || [];
}

export default function Insights() {
  const [synthesis, setSynthesis] = useState(null);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeState, setActiveState] = useState('ground');
  const [expandedStates, setExpandedStates] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [synthesisRes, previewRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/synthesis`, { credentials: 'include' }),
        fetch(`${API_URL}/api/synthesis/preview`, { credentials: 'include' }),
        fetch(`${API_URL}/api/dashboard/stats`, { credentials: 'include' })
      ]);

      if (synthesisRes.ok) {
        const data = await synthesisRes.json();
        setSynthesis(data);
      }
      if (previewRes.ok) {
        const data = await previewRes.json();
        setPreview(data);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    setPurchasing(true);
    try {
      const response = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          feature: 'oneview',
          origin_url: window.location.origin
        })
      });

      if (!response.ok) throw new Error('Failed to create checkout');

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      toast.error('Failed to start checkout');
    } finally {
      setPurchasing(false);
    }
  };

  const handleCreateSynthesis = async () => {
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/synthesis/create`, {
        method: 'POST',
        credentials: 'include'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create synthesis');
      }

      const data = await response.json();
      setSynthesis(data);
      toast.success('Strata Record Synthesis complete!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const hasPurchased = stats?.purchased_features?.includes('oneview');
  const canSynthesize = preview?.can_synthesize;
  const mapScores = synthesis?.state_scores || preview?.state_scores || {
    ground: 0, observe: 0, connect: 0, execute: 0, integrate: 0
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="w-12 h-12 border-4 border-amber-600/30 border-t-amber-600 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="insights-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-white mb-2">Insights</h1>
            <p className="text-slate-400">
              {synthesis 
                ? 'Your Strata Record Synthesis reveals the strata of your leadership substrate — where you are strong, where you are growing, and where your fluidity is expanding.'
                : 'Unlock powerful AI-driven insights from your leadership data.'
              }
            </p>
          </div>
          
          {!synthesis && hasPurchased && canSynthesize && (
            <Button 
              onClick={handleCreateSynthesis}
              disabled={creating}
              className="strata-button-primary"
              data-testid="create-synthesis-btn"
            >
              {creating ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Creating Synthesis...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Strata Record Synthesis
                </>
              )}
            </Button>
          )}
        </div>

        {/* Not purchased state */}
        {!synthesis && !hasPurchased && (
          <Card className="strata-card-accent">
            <CardContent className="p-8 lg:p-12">
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-600/20 text-amber-500 text-sm mb-4">
                    <Lock className="w-4 h-4" />
                    Premium Feature
                  </div>
                  <h2 className="font-display text-2xl lg:text-3xl text-white mb-4">
                    Strata Record Synthesis
                  </h2>
                  <p className="text-slate-400 mb-6 leading-relaxed">
                    Unlock AI-powered analysis that synthesizes all your leadership data into unified insights. 
                    Discover verified signals, growth edges, and your unique Strata Record Signature.
                  </p>
                  
                  {!canSynthesize && (
                    <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-600/10 border border-amber-600/20 mb-6">
                      <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                      <div>
                        <p className="text-amber-500 font-medium">Need more sources</p>
                        <p className="text-slate-400 text-sm">
                          Add {3 - (preview?.source_count || 0)} more processed sources to unlock synthesis.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3 mb-8">
                    {['Strata Record Signature - your leadership essence in one sentence',
                      'Verified Signals - patterns confirmed across multiple sources',
                      'Growth Edges - where your next bloom is waiting',
                      'State Narratives - deep dive into each leadership dimension'
                    ].map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-amber-500" />
                        <span className="text-slate-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    onClick={handlePurchase}
                    disabled={purchasing || !canSynthesize}
                    className="strata-button-primary text-lg px-8 py-6"
                    data-testid="purchase-oneview-btn"
                  >
                    {purchasing ? 'Processing...' : 'Unlock for $29'}
                  </Button>
                </div>
                
                <div className="relative">
                  <div className="absolute inset-0 blur-3xl opacity-20 bg-gradient-to-br from-amber-500 to-state-connect" />
                  <div className="relative flex justify-center">
                    <StrataMap scores={mapScores} size={380} animated={false} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Purchased but not created */}
        {!synthesis && hasPurchased && !canSynthesize && (
          <Card className="strata-card">
            <CardContent className="py-12 text-center">
              <AlertCircle className="w-12 h-12 mx-auto text-amber-500 mb-4" />
              <h3 className="font-display text-xl text-white mb-2">Need More Sources</h3>
              <p className="text-slate-400 mb-4">
                Add {3 - (preview?.source_count || 0)} more processed sources to generate your synthesis.
              </p>
              <Button variant="outline" onClick={() => window.location.href = '/sources'} className="strata-button-secondary">
                Go to Sources
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Synthesis View */}
        {synthesis && (
          <div className="space-y-8">
            {/* Strata Record Signature */}
            <Card className="strata-card-accent">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="w-6 h-6 text-amber-500" />
                  <span className="text-amber-500 font-mono text-sm">STRATA SIGNATURE</span>
                </div>
                <p className="font-display text-2xl lg:text-3xl text-white leading-relaxed">
                  "{synthesis.strata_signature}"
                </p>
              </CardContent>
            </Card>

            {/* Map + State Tabs */}
            <div className="grid lg:grid-cols-2 gap-8">
              <Card className="strata-card">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-white">Your Strata Record Map</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-center">
                    <StrataMap 
                      scores={synthesis.state_scores} 
                      size={400} 
                      animated={true}
                      activeState={activeState}
                      onStateClick={(state) => setActiveState(state)}
                      onStateHover={(state) => state && setActiveState(state)}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="strata-card">
                <CardHeader className="pb-2">
                  <CardTitle className="font-display text-xl text-white">State Narratives</CardTitle>
                </CardHeader>
                <CardContent>
                  <Tabs value={activeState} onValueChange={setActiveState} className="w-full">
                    {/* Improved Tab Navigation */}
                    <TabsList className="w-full bg-transparent border-b border-slate-700 rounded-none p-0 mb-6 h-auto">
                      {Object.entries(stateDetails).map(([key, { label, color }]) => {
                        const isActive = activeState === key;
                        return (
                          <TabsTrigger 
                            key={key} 
                            value={key}
                            className="flex-1 rounded-none border-b-[3px] bg-transparent py-3 transition-all duration-300 data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                            style={{ 
                              borderBottomColor: isActive ? color : 'transparent',
                              opacity: isActive ? 1 : 0.5
                            }}
                          >
                            <div 
                              className="w-2 h-2 rounded-full mr-2"
                              style={{ backgroundColor: color }} 
                            />
                            <span 
                              className="hidden sm:inline transition-all"
                              style={{ 
                                fontSize: isActive ? '14px' : '12px',
                                fontWeight: isActive ? 500 : 400,
                                color: isActive ? '#fff' : '#9CA3AF'
                              }}
                            >
                              {label}
                            </span>
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                    
                    {/* State Narrative Content with New Hierarchy */}
                    {Object.entries(stateDetails).map(([key, { label, color, colorDark, desc }]) => {
                      const narrative = synthesis?.state_narratives?.[key] || '';
                      const parsed = parseNarrative(narrative);
                      const tags = getStateTags(synthesis, key);
                      const isExpanded = expandedStates[key];
                      const tagColor = colorDark || color;
                      
                      return (
                        <TabsContent key={key} value={key} className="mt-0 animate-fade-in">
                          <div className="space-y-5">
                            {/* State Header with Score */}
                            <div className="flex items-center gap-3">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ 
                                  backgroundColor: color,
                                  boxShadow: `0 0 10px ${color}50`
                                }} 
                              />
                              <h3 className="font-display text-lg text-white">{label}</h3>
                              <span className="font-mono text-lg text-amber-500">{synthesis?.state_scores?.[key] || 0}</span>
                            </div>
                            
                            {/* Layer 1: The Headline */}
                            <p 
                              className="font-medium leading-snug"
                              style={{ 
                                fontSize: '16px', 
                                color: color,
                                lineHeight: 1.4
                              }}
                            >
                              {parsed.headline || stateDetails[key]?.defaultHeadline || `Your ${label.toLowerCase()} dimension reflects your leadership identity.`}
                            </p>
                            
                            {/* Layer 2: The Body */}
                            <div className="space-y-4">
                              {parsed.paragraphs.length > 0 ? (
                                parsed.paragraphs.map((para, idx) => (
                                  <p 
                                    key={idx}
                                    style={{ 
                                      fontSize: '14px', 
                                      color: '#C8C0B0',
                                      lineHeight: 1.75
                                    }}
                                  >
                                    {para}
                                  </p>
                                ))
                              ) : (
                                <p 
                                  style={{ 
                                    fontSize: '14px', 
                                    color: '#C8C0B0',
                                    lineHeight: 1.75
                                  }}
                                >
                                  {desc}. Add more sources to generate a detailed narrative for this dimension.
                                </p>
                              )}
                              
                              {/* Read More */}
                              {parsed.truncated && !isExpanded && (
                                <button
                                  onClick={() => setExpandedStates(prev => ({ ...prev, [key]: true }))}
                                  className="flex items-center gap-1 text-amber-500 text-sm hover:text-amber-400 transition-colors"
                                >
                                  Read more
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                              )}
                              
                              {/* Expanded content */}
                              {parsed.truncated && isExpanded && (
                                <p 
                                  style={{ 
                                    fontSize: '14px', 
                                    color: '#C8C0B0',
                                    lineHeight: 1.75
                                  }}
                                  className="animate-fade-in"
                                >
                                  {parsed.fullText.split(' ').slice(parsed.paragraphs.join(' ').split(' ').length).join(' ')}
                                </p>
                              )}
                            </div>
                            
                            {/* Layer 3: Signal Tags */}
                            <div className="flex flex-wrap gap-2 pt-2">
                              {tags.map((tag, idx) => (
                                <span
                                  key={idx}
                                  className="px-3 py-1 rounded-full text-xs font-medium border"
                                  style={{
                                    backgroundColor: `${tagColor}20`,
                                    color: color,
                                    borderColor: `${tagColor}50`
                                  }}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </TabsContent>
                      );
                    })}
                  </Tabs>
                </CardContent>
              </Card>
            </div>

            {/* Verified Signals */}
            {synthesis.verified_signals?.length > 0 && (
              <Card className="strata-card">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    Verified Signals
                  </CardTitle>
                  <p className="text-slate-400 text-sm">
                    Patterns confirmed across multiple assessment sources
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {synthesis.verified_signals.map((signal, idx) => (
                      <div 
                        key={idx}
                        className="p-4 rounded-lg bg-green-500/5 border border-green-500/20"
                      >
                        <p className="text-white mb-2">{signal.signal}</p>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-xs ${
                            signal.strength === 'high' 
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-amber-500/20 text-amber-400'
                          }`}>
                            {signal.strength} confidence
                          </span>
                          {signal.sources && (
                            <span className="text-slate-500 text-xs">
                              {signal.sources.length} sources
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Growth Edges (formerly Fluidity Constraints) */}
            {synthesis.development_tensions?.length > 0 && (
              <Card className="strata-card">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Growth Edges
                  </CardTitle>
                  <p className="text-slate-400 text-sm">
                    Where your next bloom is waiting — strata with unrealised potential
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {synthesis.development_tensions.map((tension, idx) => (
                      <div 
                        key={idx}
                        className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20"
                      >
                        <p className="text-white font-medium mb-3">{tension.tension}</p>
                        <div className="grid md:grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-slate-500 text-xs mb-1">Your Perspective</p>
                            <p className="text-slate-300 text-sm">{tension.self_view}</p>
                          </div>
                          <div>
                            <p className="text-slate-500 text-xs mb-1">Others' Perspective</p>
                            <p className="text-slate-300 text-sm">{tension.others_view}</p>
                          </div>
                        </div>
                        {tension.recommendation && (
                          <div className="pt-3 border-t border-amber-500/20">
                            <p className="text-slate-500 text-xs mb-1">Invitation to Cultivate</p>
                            <p className="text-amber-400 text-sm">{tension.recommendation}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Growth Trajectory */}
            {synthesis.growth_trajectory && (
              <Card className="strata-card">
                <CardHeader>
                  <CardTitle className="font-display text-xl text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-state-integrate" />
                    Growth Trajectory
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-8">
                    <div>
                      <p className="text-slate-400 text-sm mb-2">Direction</p>
                      <p className="text-white leading-relaxed">{synthesis.growth_trajectory.direction}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Momentum</p>
                      <span className={`inline-flex px-3 py-1 rounded-full text-sm uppercase tracking-wider font-medium ${
                        synthesis.growth_trajectory.momentum === 'accelerating' 
                          ? 'bg-green-500/20 text-green-400'
                          : synthesis.growth_trajectory.momentum === 'steady'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-amber-500/20 text-amber-400'
                      }`}>
                        {synthesis.growth_trajectory.momentum === 'emerging' ? 'emerging fluidity' : 
                         synthesis.growth_trajectory.momentum === 'steady' ? 'strong fluidity' : 
                         synthesis.growth_trajectory.momentum}
                      </span>
                    </div>
                    <div>
                      <p className="text-slate-400 text-sm mb-2">Key Themes</p>
                      <ul className="space-y-1.5">
                        {synthesis.growth_trajectory.key_themes?.map((theme, idx) => (
                          <li key={idx} className="text-slate-300 text-sm flex items-start gap-2">
                            <span className="text-amber-500 mt-0.5">•</span>
                            <span>{theme}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
